import clsx from "clsx";
import { FC, useState } from "react";

import { EditableField } from "components/hover-widgets/EditableField";
import { SessionInProgressChip } from "components/materials/home/SessionInProgressButton";
import { SectionIcon } from "components/materials/section/SectionIcon";
import { SectionSidebarOptionsMenu } from "components/menus/SectionSidebarOptionsMenu";
import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { Tooltip } from "mds/components/Tooltip";
import { LinkIcon } from "mds/icons";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { onTitleChanged } from "utils/store-utils";
import { sectionHomeUrl } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

type AdminSectionListItemProps = {
  className?: string;
  section: SectionType;
  canEdit: boolean;
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  linkedSectionsCount?: number;
  optionsMenuActions: {
    onSettings: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onDuplicateAsTemplate?: () => void;
    onCopyTo: (sectionId: string, courseId?: string, termId?: string) => void;
    onMoveTo: (courseId: string, termId: string) => void;
    onExport: () => void;
    onArchive: () => void;
    onUnarchive: () => void;
  };
};

export const AdminSectionListItem: FC<AdminSectionListItemProps> = ({
  className,
  section,
  canEdit,
  eventPrefix,
  eventProperties,
  linkedSectionsCount = 0,
  optionsMenuActions,
}) => {
  const { isArchived } = useIsArchived();
  const ccmEnabled = isCCMEnabled();
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);

  return (
    <ListItem
      className={clsx("justify-between", className)}
      eventName={`${eventPrefix}Section selected`}
      eventProperties={{ ...eventProperties, sectionTitle: section.title }}
      kind="primary"
      to={sectionHomeUrl(section.id)}
    >
      <div className="body-s flex w-full min-w-12 items-center justify-between gap-2">
        <div
          className={clsx("flex w-full min-w-12 items-center gap-2", {
            "text-black-tint-55": isArchived(section),
          })}
        >
          <SectionIcon section={section} />

          {isEditingTitle ? (
            <EditableField
              className="w-full min-w-12 truncate"
              label={t("glossary.section_title")}
              textSize="s"
              value={section.title}
              isEditing
              onSetIsEditing={setIsEditingTitle}
              onValueChanged={(newTitle) =>
                onTitleChanged("sections", section.id, newTitle, {
                  eventName: "Section - Change title",
                })
              }
            />
          ) : (
            <div>{section.title}</div>
          )}

          {ccmEnabled && linkedSectionsCount > 0 && (
            <Tooltip
              text={t("section_list_panel.sections_linked_to_template", {
                count: linkedSectionsCount,
              })}
            >
              <IconText
                className="body-xs gap-1 text-black-tint-55"
                iconEnd={<LinkIcon />}
                text={linkedSectionsCount.toString()}
              />
            </Tooltip>
          )}
        </div>

        <SessionInProgressChip sectionId={section.id} />

        {canEdit && (
          <SectionSidebarOptionsMenu
            optionsMenuActions={{ ...optionsMenuActions, onRename: () => setIsEditingTitle(true) }}
            section={section}
          />
        )}
      </div>
    </ListItem>
  );
};
